/**
 * WireStatusStepper - reusable 4-step stepper for wire_status progression.
 * pending → initiated → received → confirmed. Refunded is rendered as a terminal state.
 */
import React from 'react';
import { Check, AlertTriangle } from 'lucide-react';
import { WireStatus } from '../types/syndication';

const STEPS: WireStatus[] = ['pending', 'initiated', 'received', 'confirmed'];
const STEP_LABELS: Record<WireStatus, string> = {
  pending: 'Pending',
  initiated: 'Initiated',
  received: 'Received',
  confirmed: 'Confirmed',
  refunded: 'Refunded',
};

interface WireStatusStepperProps {
  status: WireStatus;
  onStepClick?: (next: WireStatus) => void;
  compact?: boolean;
}

const WireStatusStepper: React.FC<WireStatusStepperProps> = ({
  status,
  onStepClick,
  compact,
}) => {
  if (status === 'refunded') {
    return (
      <div className="inline-flex items-center text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
        <AlertTriangle className="w-3.5 h-3.5 mr-1" />
        Refunded
      </div>
    );
  }

  const currentIdx = STEPS.indexOf(status);
  const size = compact ? 'w-5 h-5 text-[10px]' : 'w-6 h-6 text-xs';

  return (
    <div className="inline-flex items-center">
      {STEPS.map((s, idx) => {
        const isPast = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        const isNext = idx === currentIdx + 1;
        const clickable = !!onStepClick && isNext;
        return (
          <React.Fragment key={s}>
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick!(s)}
              className={`flex flex-col items-center ${
                clickable ? 'cursor-pointer' : 'cursor-default'
              }`}
              title={STEP_LABELS[s]}
            >
              <span
                className={`${size} rounded-full flex items-center justify-center font-semibold border-2 ${
                  isPast
                    ? 'bg-green-500 text-white border-transparent'
                    : isCurrent
                    ? 'bg-blue-600 text-white border-transparent'
                    : clickable
                    ? 'bg-white text-blue-600 border-blue-400 hover:bg-blue-50'
                    : 'bg-white text-gray-400 border-gray-300'
                }`}
              >
                {isPast ? <Check className="w-3 h-3" /> : idx + 1}
              </span>
              {!compact && (
                <span
                  className={`text-[10px] mt-1 ${
                    isCurrent
                      ? 'text-blue-700 font-medium'
                      : isPast
                      ? 'text-green-700'
                      : 'text-gray-500'
                  }`}
                >
                  {STEP_LABELS[s]}
                </span>
              )}
            </button>
            {idx < STEPS.length - 1 && (
              <div
                className={`h-0.5 w-6 mx-0.5 ${
                  idx < currentIdx ? 'bg-green-400' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default WireStatusStepper;
